import React from "react";
import { Box, Tooltip, Typography, styled } from "@mui/material";
import type { CareerCluster } from "../../types";

interface ClusterAvatarGroupProps {
  clusters: CareerCluster[];
  onClusterClick?: (clusterId: string) => void;
}

const Group = styled(Box, {
  label: "ClusterAvatarGroup",
})(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
}));

const Avatar = styled(Box, {
  label: "ClusterAvatar",
  shouldForwardProp: (prop) =>
    prop !== "overlap" && prop !== "depth" && prop !== "clickable",
})<{ overlap?: boolean; depth: number; clickable?: boolean }>(
  ({ overlap, depth, clickable }) => ({
    width: 48,
    height: 48,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    // Leftmost avatar sits on top; each subsequent one tucks behind.
    marginRight: overlap ? -6 : 0,
    zIndex: depth,
    cursor: clickable ? "pointer" : "default",
    "& img": {
      width: 48,
      height: 48,
      objectFit: "contain",
    },
  }),
);

export const ClusterAvatarGroup = ({
  clusters,
  onClusterClick,
}: ClusterAvatarGroupProps) => {
  if (!clusters || clusters.length === 0) return null;

  return (
    <Group>
      {clusters.map((cluster, index) => (
        <Tooltip key={cluster.id} title={cluster.name} placement="bottom">
          <Avatar
            overlap={index < clusters.length - 1}
            depth={clusters.length - index}
            clickable={!!onClusterClick}
            onClick={
              onClusterClick ? () => onClusterClick(cluster.id) : undefined
            }
            role={onClusterClick ? "button" : undefined}
            aria-label={cluster.name}
          >
            {cluster.icon?.url ? (
              <img src={cluster.icon.url} alt="" />
            ) : (
              <Typography sx={{ fontSize: 16, color: "#1c252e" }}>
                {cluster.name.charAt(0).toUpperCase()}
              </Typography>
            )}
          </Avatar>
        </Tooltip>
      ))}
    </Group>
  );
};
